.header {
  position: sticky;
  inset-block-start: 0;
  z-index: 10;
  border-block-end: 1px solid var(--border);
  background: color-mix(in srgb, var(--bg) 88%, transparent);
  backdrop-filter: blur(8px);
}

/*
 * The masthead row, and the one property that makes it a row at all (`APP-022`).
 *
 * **`flex-wrap` is load-bearing and was missing.** `.today` below asks for `flex-basis: 100%`
 * so canon § 8's date line takes a line of its own on a phone — and on a *non-wrapping* flex
 * row a 100%-basis item cannot wrap. It shrinks instead, and squeezes the identity and the
 * controls beside it into slivers; `.today`'s `order: 3` never took effect either, for the
 * same reason. That was the broken bar at 390px, and it is why this is a one-property fix
 * with a paragraph attached rather than a layout rewrite.
 *
 * It passed the gate it had. `e2e/tests/surface-chrome.spec.ts` asserts the desk causes no
 * horizontal overflow at 390px, and a squashed row overflows nothing — so the assertion held
 * while the bar was unreadable. `test/design/desk-masthead.test.ts` is the one that would
 * have failed.
 */
.inner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  padding-block: var(--space-3);
}

.identity {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-inline-size: 0;
}

/*
 * The wordmark, and the mark before it. `contracts/DESIGN-TOKENS.md` § Shared component
 * patterns fixes both for every surface, so a founder crossing from the board to their desk
 * is not handed a second logo for one product.
 *
 * The brass rule this wordmark used to sit on is withdrawn with the pattern: brass means rank
 * marks, section rules and disclosure, and a masthead wearing it made Dealbid's own name look
 * like a position somebody had won.
 */
.wordmark {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-family: var(--font-display);
  font-size: var(--text-xl);
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--text);
  text-decoration: none;
}

/*
 * The brandmark, in the accent rather than in brass — for the same reason the rule came
 * off: brass on the logo reads as a won position. `D-013` replaced the rank-01 tile that
 * used to stand here, which had the same problem one step further along: the logo was a
 * rank, not merely coloured like one.
 */
.mark {
  /*
   * contracts/DESIGN-TOKENS.md § Shared component patterns fixes the height and lets the
   * width follow. The mark is portrait, so sizing it by width instead would make it tall
   * enough to break the header — which is the regression e2e/tests/surface-chrome.spec.ts
   * exists to catch.
   */
  display: block;
  block-size: 1.625rem;
  inline-size: auto;
  flex: none;
  /* The mark's path fills from currentColor, so this one line themes it in both modes. */
  color: var(--accent);
}

/*
 * The surface tag. A seller works two Dealbid sites at once — the public board and their
 * own desk — so the desk says so at every width, and says it in brass rather than in the
 * action green that means "you can press this".
 */
.surface {
  padding-block: 2px;
  padding-inline: var(--space-2);
  border: 1px solid var(--brass-rule);
  border-radius: var(--radius-sm);
  background: var(--brass-soft);
  color: var(--brass);
  font-family: var(--font-figure);
  font-size: var(--text-xs);
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.skipLink {
  position: absolute;
  inset-inline-start: var(--space-4);
  inset-block-start: -100%;
  z-index: 20;
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-md);
  background: var(--surface-elevated);
  color: var(--text);
  box-shadow: var(--shadow-elevated);
  text-decoration: none;
}

.skipLink:focus {
  inset-block-start: var(--space-3);
}

/*
 * `Submit a deal`, at the end of the row (`APP-017`, narrowed by `APP-022`). The theme
 * control left this group for the footer one task ago and sign-out has now followed it, so
 * what is left is the single action a founder came to the desk to take.
 */
.controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--space-2);
}

/*
 * The UTC date line (`APP-016`, and the line `APP-022` finally gave a line).
 *
 * Muted and in the figure face, like every other date on the desk. It takes the whole row
 * below 48rem rather than being dropped there, which is where the drawing leaves it: canon
 * § 8's statement is not a desktop-only obligation (design record, departure D6).
 *
 * This rule was already written that way and had never worked, because `.inner` did not
 * wrap — see the note there. The declarations below are unchanged; what changed is that they
 * now do what they say.
 */
.today {
  flex-basis: 100%;
  order: 3;
  margin: 0;
  color: var(--text-muted);
  font-size: var(--text-xs);
}

@media (min-width: 48rem) {
  .today {
    flex-basis: auto;
    order: 0;
    margin-inline-end: auto;
  }
}
